import { Button } from 'admin/components/atoms/Buttons';
import ShopSelector from 'admin/components/organisms/ShopSelector';
import { COLOR } from 'admin/constants/styles';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fetchCoupon } from '~/javascripts/admin/actions/coupon';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { components } from '~/javascripts/admin/schema/docs/api/admin';
import { formatUUID } from '~/javascripts/admin/utils';
import DateTimeRange from '../../atoms/DateTimeRange';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import LocalizedMask from '../../atoms/LocalizedMask';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import TextArea from '../../atoms/TextArea';
import TextBox from '../../atoms/TextBox';
import Tooltip from '../../atoms/Tooltip';
import LabeledRadio from '../../molecules/LabeledRadio';
import QrCodeDrawer from '../../molecules/QrCodeDrawer';
import ImgUploader from '../../organisms/ImgUploader';
import SelectedShopsEditor from './SelectedShopsEditor';

type Shop = components['schemas']['Shop'];

type PrivateMoney = components['schemas']['PrivateMoney'];

type Coupon = Awaited<ReturnType<typeof fetchCoupon>>;

type DateRange = {
  from: Moment | null;
  to: Moment | null;
};

type CouponFormData = {
  id: string;
  name: string;
  description?: string;
  issued_shop?: Shop;
  private_money?: PrivateMoney;
  dateRange: DateRange;
  displayDateRange: DateRange;
  discount_amount?: number | null;
  discount_percentage?: string | null;
  discount_upper_limit?: number | null;
  min_amount?: number | null;
  usage_limit?: number | null;
  is_shop_specified?: boolean;
  available_shops?: Shop[];
  is_public?: boolean;
  is_hidden?: boolean;
  code?: string | null;
  coupon_image?: File | Blob | string | null;
  is_disabled?: boolean;
  token?: string;
  num_recipients_cap?: number | null;
  num_recipients?: number | null;
};

type EditCouponProps = {
  coupon: Coupon;
  onSubmit: (coupon: Coupon) => void;
  mode: 'create' | 'edit';
};

const DummyQrCode = styled.div`
  margin: 20px 10px;
  width: 200px;
  height: 200px;
  background: lightGray;
`;

const FormWrapper = styled.div``;

const FormGrid = styled(Grid.Container)`
  ${Headings.h6}, ${DateTimeRange}, ${QrCodeDrawer} {
    margin: 0;
  }

  ${Label} {
    margin-bottom: 0;
  }
`;

const Header = styled.div`
  display: flex;
  gap: 2px;
`;

const TooltipHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DiscountPercentageAndUpperLimitWrapper = styled.div`
  display: flex;
  margin-left: 1rem;
`;

const Msg = styled.div`
  font-size: 0.8rem;
  color: ${COLOR.errorMessage};
`;

const QRCodeDrawerWrapper = styled.div`
  margin-top: 1rem;
`;

const RecipientsWrapper = styled.div`
  width: 432px;
  display: flex;
  gap: 16px;
  margin-top: 8px;

  > div {
    flex: 1;
  }
`;

const SelectOptionWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacing['4']};
  `}
`;

const CopyTextWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacing['12']};
  `}
`;

const CircleStatusIndicator = styled.div<{ $isActive: boolean }>`
  ${({ theme: { colors, borderRadius }, $isActive }) => {
    const backgroundColor = $isActive ? colors.green['300'] : colors.gray['300'];

    return css`
      width: 16px;
      height: 16px;
      border-radius: ${borderRadius.full};
      display: inline-block;
      background-color: ${backgroundColor};
    `;
  }}
`;

const statusOptions = [
  { label: '有効', value: true },
  { label: '無効', value: false },
];

const formatOptionLabel = (option: (typeof statusOptions)[number]) => {
  return (
    <SelectOptionWrapper>
      <CircleStatusIndicator $isActive={option.value} />
      <div>{option.label}</div>
    </SelectOptionWrapper>
  );
};

const ErrorMsg = ({ error }: { error?: string }) => {
  return error ? <Msg>{error}</Msg> : <></>;
};

const EditIssuedShop = ({
  form,
  onChangeShop,
  onChangeMoney,
}: {
  form: { id?: string; issued_shop?: Shop; private_money?: PrivateMoney };
  onChangeShop: (shop: Shop) => void;
  onChangeMoney: (money: PrivateMoney) => void;
}) => {
  return (
    <>
      {form.id ? (
        form.issued_shop && `${form.issued_shop.name}(${form.private_money?.name})`
      ) : (
        <ShopSelector onChangeShop={onChangeShop} onChangeMoney={onChangeMoney} />
      )}
    </>
  );
};

const EditCoupon = ({ coupon, onSubmit, mode }: EditCouponProps) => {
  const user = useAppSelector((state) => state.user);
  const isMerchantUser =
    !user.isServiceUser?.() &&
    !!coupon?.private_money &&
    !user.isIssuerOfMoney?.(coupon.private_money);

  const formDefaultValues: CouponFormData = {
    ...coupon,
    discount_amount: coupon?.discount_amount ?? null,
    discount_percentage: coupon?.discount_percentage?.toString() ?? null,
    discount_upper_limit: coupon?.discount_upper_limit ?? null,
    min_amount: coupon?.min_amount ?? null,
    usage_limit: coupon?.usage_limit ?? null,
    dateRange: {
      from: coupon.starts_at ? moment(coupon.starts_at) : null,
      to: coupon.ends_at ? moment(coupon.ends_at) : null,
    },
    displayDateRange: {
      from: coupon.display_starts_at ? moment(coupon.display_starts_at) : null,
      to: coupon.display_ends_at ? moment(coupon.display_ends_at) : null,
    },
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    clearErrors,
  } = useForm<CouponFormData>({
    defaultValues: formDefaultValues,
  });

  const [qrSize] = useState<number>(128);
  const [isDiscountAmount, setIsDiscountAmount] = useState<boolean>(
    coupon.discount_amount != null || coupon.discount_percentage == null
  );

  const formValues = watch();

  const isEdit = (): boolean => mode !== 'create';

  const generateCouponCode = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array.from({ length: 8 }, () =>
      chars.charAt(Math.floor(Math.random() * chars.length))
    ).join('');
  };

  const onSubmitForm = (data: CouponFormData): void => {
    const {
      dateRange,
      displayDateRange,
      discount_percentage,
      num_recipients_cap,
      num_recipients,
      ...rest
    } = data;

    const params = {
      ...rest,
      discount_percentage: discount_percentage ? parseFloat(discount_percentage) : null,
      starts_at: data.dateRange.from ? data.dateRange.from.toISOString() : null,
      ends_at: data.dateRange.to ? data.dateRange.to.toISOString() : null,
      display_starts_at: data.displayDateRange.from
        ? data.displayDateRange.from.toISOString()
        : null,
      display_ends_at: data.displayDateRange.to ? data.displayDateRange.to.toISOString() : null,
      coupon_image:
        data.coupon_image instanceof Blob && !(data.coupon_image instanceof File)
          ? data.coupon_image
          : null,
      num_recipients_cap: num_recipients_cap || null,
    };

    onSubmit(params);
  };

  const validatePeriod = (dateRange: DateRange): boolean => {
    if (!dateRange.from || !dateRange.to) return true;
    return dateRange.from.isSameOrBefore(dateRange.to);
  };

  useEffect(() => {
    reset({
      ...coupon,
      discount_amount: coupon?.discount_amount ?? null,
      discount_percentage: coupon?.discount_percentage?.toString() ?? null,
      discount_upper_limit: coupon?.discount_upper_limit ?? null,
      min_amount: coupon?.min_amount ?? null,
      usage_limit: coupon?.usage_limit ?? null,
      dateRange: {
        from: coupon.starts_at ? moment(coupon.starts_at) : null,
        to: coupon.ends_at ? moment(coupon.ends_at) : null,
      },
      displayDateRange: {
        from: coupon.display_starts_at ? moment(coupon.display_starts_at) : null,
        to: coupon.display_ends_at ? moment(coupon.display_ends_at) : null,
      },
    });

    if (isEdit())
      setIsDiscountAmount(coupon.discount_amount != null || coupon.discount_percentage == null);
  }, [coupon, reset]);

  return (
    <FormWrapper>
      <FormGrid columnGap={2} rowGap={8}>
        <Grid.Item size={2}>
          <TooltipHeaderWrapper>
            <Header>
              <Label>状態</Label>
            </Header>
            <Tooltip id="coupon-disabled" text="クーポンを無効にすると使用不可となる" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={7}>
          <Controller
            name="is_disabled"
            control={control}
            render={({ field }) => (
              <div style={{ width: '200px' }}>
                <Select
                  isMulti={false}
                  fullWidth
                  options={statusOptions}
                  value={statusOptions.find((x) => x.value === !field.value)}
                  formatOptionLabel={formatOptionLabel}
                  isDisabled={isMerchantUser}
                  onChange={(option) => field.onChange(!option?.value)}
                />
              </div>
            )}
          />
        </Grid.Item>

        <Grid.Divider />

        {isEdit() && (
          <>
            <Grid.Item size={2}>
              <Header>
                <Label>クーポンID</Label>
              </Header>
            </Grid.Item>
            <Grid.Item size={10}>{`${formatUUID(coupon.id || '')} (フルID: ${
              coupon.id
            })`}</Grid.Item>

            <Grid.Divider />
          </>
        )}

        <Grid.Item size={2}>
          <TooltipHeaderWrapper>
            <Header>
              <Label>発行店舗</Label> <Required />
            </Header>
            <Tooltip id="coupon-issuer-shop" text="クーポンを発行している店舗" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={7} flexDirection="column">
          <Controller
            name="issued_shop"
            control={control}
            rules={{
              validate: (value) => !!value?.id || '発行店舗を指定して下さい',
            }}
            render={({ field }) => (
              <EditIssuedShop
                form={formValues}
                onChangeShop={(shop) => field.onChange(shop)}
                onChangeMoney={(money) => setValue('private_money', money)}
              />
            )}
          />
          <ErrorMsg error={errors.issued_shop?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2} nextRow>
          <TooltipHeaderWrapper>
            <Header>
              <Label>クーポン名</Label> <Required />
            </Header>
            <Tooltip id="coupon-name" text="クーポンの名前としてユーザに表示されるタイトル" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={7} flexDirection="column">
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'クーポン名は必須です',
              maxLength: { value: 128, message: 'クーポン名は最大128文字までです' },
            }}
            render={({ field }) => (
              <TextBox
                {...field}
                syncValue={field.value || ''}
                placeholder="クーポン名を記載"
                size={90}
                disabled={isMerchantUser}
              />
            )}
          />
          <ErrorMsg error={errors.name?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2} nextRow alignItems="baseline">
          <TooltipHeaderWrapper>
            <Header>
              <Label>説明</Label>
            </Header>
            <Tooltip id="coupon-description" text="クーポンの詳細をユーザに伝えるための文章" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={7} flexDirection="column">
          <Controller
            name="description"
            control={control}
            rules={{
              maxLength: { value: 256, message: '説明文は最大256文字までです' },
            }}
            render={({ field }) => (
              <TextArea
                cols={90}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.currentTarget.value)}
                maxLength={300}
                placeholder="クーポン説明文を記載"
                disabled={isMerchantUser}
              />
            )}
          />
          <ErrorMsg error={errors.description?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2} nextRow>
          <TooltipHeaderWrapper>
            <Header>
              <Label>利用期間</Label> <Required />
            </Header>
            <Tooltip id="coupon-available-period" text="クーポンが利用可能な期間" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <Controller
            name="dateRange"
            control={control}
            rules={{
              validate: {
                required: (value) => (!!value.from && !!value.to) || '開始日時と終了日時は必須です',
                validPeriod: (value) => validatePeriod(value) || '期間が不正です',
              },
            }}
            render={({ field: { value, onChange } }) => (
              <DateTimeRange
                dateRange={value}
                setDateRange={onChange}
                dateTimeProps={{
                  toProps: {
                    showIcon: true,
                    timeFormat: 'HH:mm',
                    inputProps: { disabled: isMerchantUser },
                  },
                  fromProps: {
                    showIcon: true,
                    timeFormat: 'HH:mm',
                    inputProps: { disabled: isMerchantUser },
                  },
                }}
              />
            )}
          />
          <ErrorMsg error={errors.dateRange?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2} alignItems="baseline">
          <TooltipHeaderWrapper>
            <Header>
              <Label>割引設定</Label> <Required />
            </Header>
            <Tooltip
              id="coupon-discount"
              text="クーポンが決済額に対して割引される金額もしくは比率"
            />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <LabeledRadio
            labelText="割引金額"
            checked={isDiscountAmount}
            setChecked={() => {
              setIsDiscountAmount(true);
              setValue('discount_percentage', null);
              setValue('discount_upper_limit', null);

              if (errors?.discount_amount || errors?.discount_upper_limit)
                clearErrors(['discount_percentage', 'discount_upper_limit']);
            }}
            labelPosition="right"
            disabled={isEdit()}
          />

          {isDiscountAmount && (
            <Controller
              name="discount_amount"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value) return '割引設定は必須です';
                  if (!!value && value <= 0) return '正の数字を指定して下さい';
                  return true;
                },
              }}
              render={({ field }) => (
                <LocalizedMask
                  value={field.value ? field.value.toString() : ''}
                  onChange={field.onChange}
                >
                  {({ value, ...inputProps }) => (
                    <TextBox
                      syncValue={value}
                      {...inputProps}
                      name={field.name}
                      disabled={!!formValues.discount_percentage || isMerchantUser}
                      style={{ marginLeft: '1rem', width: '408px' }}
                    />
                  )}
                </LocalizedMask>
              )}
            />
          )}

          <LabeledRadio
            labelText="割引率（上限金額）"
            checked={!isDiscountAmount}
            setChecked={() => {
              setIsDiscountAmount(false);
              setValue('discount_amount', null);

              if (errors?.discount_amount) clearErrors('discount_amount');
            }}
            labelPosition="right"
            disabled={isEdit()}
          />
          {!isDiscountAmount && (
            <DiscountPercentageAndUpperLimitWrapper>
              <Controller
                name="discount_percentage"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === null) return '割引設定は必須です';
                    if (!!value && parseFloat(value) <= 0) return '正の数字を指定して下さい';
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextBox
                    name={field.name}
                    syncValue={field.value?.toString() || ''}
                    onChange={(e) => {
                      const { value } = e.target;

                      if (value === '') {
                        field.onChange(null);
                        return;
                      }

                      const inputFormatRegex =
                        /^(0(\.0?)?|0\.0[1-9]|0\.[1-9][0-9]?|[1-9](\.[0-9]{0,2})?|[1-9][0-9](\.[0-9]{0,2})?|100)$/;

                      if (!inputFormatRegex.test(value)) {
                        return;
                      }

                      field.onChange(value);
                    }}
                    onBlur={(e) => {
                      if (e.target.value.endsWith('.')) {
                        const valueWithoutTrailingDot = e.target.value.slice(0, -1);
                        field.onChange(valueWithoutTrailingDot);
                      }
                    }}
                    unit="%"
                    disabled={!!formValues.discount_amount || isMerchantUser}
                  />
                )}
              />
              <Controller
                name="discount_upper_limit"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === 0)
                      return '割引上限金額を指定する場合は正の数字で指定してください';
                    return true;
                  },
                }}
                render={({ field }) => (
                  <LocalizedMask value={field.value?.toString() || ''} onChange={field.onChange}>
                    {({ value, ...inputProps }) => (
                      <TextBox
                        syncValue={value}
                        {...inputProps}
                        name={field.name}
                        placeholder="割引上限金額なし"
                        disabled={!!formValues.discount_amount || isMerchantUser}
                      />
                    )}
                  </LocalizedMask>
                )}
              />
            </DiscountPercentageAndUpperLimitWrapper>
          )}
          <ErrorMsg error={errors.discount_amount?.message} />
          <ErrorMsg error={errors.discount_percentage?.message} />
          <ErrorMsg error={errors.discount_upper_limit?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2}>
          <TooltipHeaderWrapper>
            <Header>
              <Label>最低決済金額</Label>
            </Header>
            <Tooltip id="coupon-min-amount" text="クーポンを適用する最低決済金額" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <Controller
            name="min_amount"
            control={control}
            rules={{
              validate: (value) => {
                if (value === 0) return '最低金額を指定する場合は正の数字で指定してください';
                return true;
              },
            }}
            render={({ field }) => (
              <LocalizedMask value={field.value?.toString() || ''} onChange={field.onChange}>
                {({ value, ...inputProps }) => (
                  <TextBox
                    syncValue={value}
                    {...inputProps}
                    name={field.name}
                    placeholder="制限なし"
                    disabled={isMerchantUser}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <ErrorMsg error={errors.min_amount?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2}>
          <Header>
            <Label>獲得条件</Label>
          </Header>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <TooltipHeaderWrapper>
            <Header>獲得可能期間</Header>
            <Tooltip
              id="coupon-delivery-period"
              text="クーポンがユーザに取得可能な状態として表示される期間。指定しない場合は利用期間と同一となる"
            />
          </TooltipHeaderWrapper>
          <Controller
            name="displayDateRange"
            control={control}
            rules={{
              validate: {
                validPeriod: (value) =>
                  !value.from || !value.to || validatePeriod(value) || '期間が不正です',
              },
            }}
            render={({ field: { value, onChange } }) => (
              <DateTimeRange
                dateRange={value}
                setDateRange={onChange}
                dateTimeProps={{
                  toProps: {
                    showIcon: true,
                    timeFormat: 'HH:mm',
                    inputProps: {
                      disabled: isMerchantUser,
                    },
                  },
                  fromProps: {
                    showIcon: true,
                    timeFormat: 'HH:mm',
                    inputProps: {
                      disabled: isMerchantUser,
                    },
                  },
                }}
              />
            )}
          />
          <ErrorMsg error={errors.displayDateRange?.message} />
          <RecipientsWrapper>
            <div>
              <Header>獲得枚数</Header>
              <Controller
                name="num_recipients_cap"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === '0') return '正の数字を指定して下さい';
                    if (mode === 'edit' && coupon.num_recipients_cap && !value)
                      return '正の数字を指定して下さい';
                    return true;
                  },
                }}
                render={({ field }) => (
                  <LocalizedMask value={field.value?.toString() || ''} onChange={field.onChange}>
                    {({ value, onChange, ...inputProps }) => (
                      <TextBox
                        syncValue={value}
                        {...inputProps}
                        name={field.name}
                        placeholder="制限なし"
                        disabled={(mode === 'edit' && !coupon.num_recipients_cap) || isMerchantUser}
                        onChange={(e) =>
                          e.target.value.replace(',', '').length <= 6 && onChange?.(e)
                        }
                      />
                    )}
                  </LocalizedMask>
                )}
              />
              <ErrorMsg error={errors.num_recipients_cap?.message} />
            </div>
            <div>
              {mode === 'edit' && coupon.num_recipients_cap && (
                <>
                  <Header>獲得済み枚数</Header>
                  <Header style={{ lineHeight: '32px' }}>{coupon.num_recipients}</Header>
                </>
              )}
            </div>
          </RecipientsWrapper>
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2}>
          <TooltipHeaderWrapper>
            <Header>
              <Label>1ユーザの使用回数</Label>
            </Header>
            <Tooltip id="coupon-usage-limit" text="１ユーザが使用出来る最大回数" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <Controller
            name="usage_limit"
            control={control}
            rules={{
              validate: (value) => {
                if (value === 0) return '使用可能回数を指定する場合は正の数字で指定してください';
                return true;
              },
            }}
            render={({ field }) => (
              <LocalizedMask
                value={field.value ? field.value.toString() : ''}
                onChange={field.onChange}
              >
                {({ value, ...inputProps }) => (
                  <TextBox
                    syncValue={value}
                    {...inputProps}
                    name={field.name}
                    placeholder="制限なし"
                    disabled={isMerchantUser}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <ErrorMsg error={errors.usage_limit?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2} alignItems="baseline">
          <TooltipHeaderWrapper>
            <Header>
              <Label>対象店舗</Label>
            </Header>
            <Tooltip id="coupon-available-shops" text="クーポンが利用可能な店舗リスト" />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <Controller
            name="is_shop_specified"
            control={control}
            render={({ field }) => (
              <>
                <LabeledRadio
                  labelText="加盟店舗全店対象"
                  checked={!field.value}
                  setChecked={() => field.onChange(false)}
                  labelPosition="right"
                  disabled={isMerchantUser}
                />
                <LabeledRadio
                  labelText="選択店舗のみ"
                  checked={!!field.value}
                  setChecked={() => field.onChange(true)}
                  labelPosition="right"
                  disabled={isMerchantUser}
                />
              </>
            )}
          />
          {formValues.is_shop_specified ? (
            <>
              <Controller
                name="available_shops"
                control={control}
                rules={{
                  validate: (value) => {
                    if (formValues.is_shop_specified && (!value || value.length === 0)) {
                      return '1つ以上の店舗を選択する必要があります';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <SelectedShopsEditor
                    coupon={coupon}
                    designatedMoney={formValues.private_money}
                    onChangeShops={field.onChange}
                    disabled={isMerchantUser}
                  />
                )}
              />
              <ErrorMsg error={errors.available_shops?.message} />
            </>
          ) : null}
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2} alignItems="baseline">
          <TooltipHeaderWrapper>
            <Header>
              <Label>アプリ配信</Label>
            </Header>
            <Tooltip
              id="coupon-delivery"
              text="クーポンをアプリ経由で配信するかどうか。アプリ配信無しとした場合はクーポン受け取りQRコード経由でしか受け取れないクーポンになる"
            />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10} flexDirection="column">
          <Controller
            name="is_public"
            control={control}
            render={({ field }) => (
              <>
                <LabeledRadio
                  labelText="配信する"
                  checked={!!field.value}
                  setChecked={() => {
                    field.onChange(true);
                    setValue('code', null);
                  }}
                  labelPosition="right"
                  disabled={isMerchantUser}
                />
                <LabeledRadio
                  labelText="配信しない（クーポンコード発行）"
                  checked={!field.value}
                  setChecked={() => {
                    field.onChange(false);
                    setValue('code', generateCouponCode());
                  }}
                  labelPosition="right"
                  disabled={isMerchantUser}
                />
              </>
            )}
          />
          {!formValues.is_public ? (
            <Controller
              name="code"
              control={control}
              rules={{
                required: !formValues.is_public ? 'クーポンコードは必須です' : false,
                maxLength: { value: 36, message: 'クーポンコードは最大36文字までです' },
              }}
              render={({ field }) => (
                <TextBox
                  name={field.name}
                  syncValue={field.value || ''}
                  onChange={field.onChange}
                  maxLength={36}
                  align="left"
                  style={{ marginLeft: '1rem' }}
                  disabled={isMerchantUser}
                />
              )}
            />
          ) : null}
          <ErrorMsg error={errors.code?.message} />
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2}>
          <TooltipHeaderWrapper>
            <Header>
              <Label>クーポン画像</Label>
            </Header>
            <Tooltip
              id="coupon-image"
              text={
                <>
                  JPG,PNGのみ設定可能
                  <br />
                  設定時に画像の利用範囲を指定します
                </>
              }
            />
          </TooltipHeaderWrapper>
        </Grid.Item>
        <Grid.Item size={10}>
          <Controller
            name="coupon_image"
            control={control}
            render={({ field }) => (
              <ImgUploader
                onChangeImg={field.onChange}
                initSrc={field.value}
                needCrop
                accept="image/jpeg, image/png"
                inputProps={{
                  disabled: isMerchantUser,
                }}
              />
            )}
          />
        </Grid.Item>

        <Grid.Divider />

        {isEdit() && !!coupon.id && (
          <>
            <Grid.Item size={2} alignItems="center" style={{ marginTop: '10px' }}>
              <Header>
                <Label>QRコード</Label>
              </Header>
            </Grid.Item>
            <Grid.Item size={10}>
              {coupon.id && (
                <QrCodeDrawer
                  uri={coupon.token || ''}
                  name={coupon.name}
                  qrSize={qrSize}
                  showUrl
                  urlAsLabel
                  copyable
                />
              )}
            </Grid.Item>

            <Grid.Divider />
          </>
        )}
      </FormGrid>
      <Button onClick={handleSubmit(onSubmitForm)} disabled={isMerchantUser}>
        {isEdit() ? '更新' : '登録'}
      </Button>
      {!isEdit() &&
        (coupon.id ? (
          <QRCodeDrawerWrapper>
            <div>下記のQRコード(画像)をご利用ください</div>
            <QrCodeDrawer
              uri={coupon.token || ''}
              name={coupon.name}
              qrSize={qrSize}
              showUrl
              urlAsLabel
              copyable
            />
          </QRCodeDrawerWrapper>
        ) : (
          <DummyQrCode>ここにQRコードが表示されます</DummyQrCode>
        ))}
    </FormWrapper>
  );
};

export default styled(EditCoupon)``;
